import { useState } from 'react';
import type { ReactNode } from 'react';
import { WEB_ICO } from '../../shell/icons';
import { WebModal } from '../../shell/WebModal';
import { ApiError, NetworkError } from '../../api/client';

function SectionTitle({ children }: { children: string }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{children}</div>
  );
}

function SettingRow({ label, onClick, danger }: { label: ReactNode; onClick?: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        background: 'none',
        border: 'none',
        borderBottom: '1px solid var(--border-default)',
        padding: '16px 2px',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'var(--font-vi)',
        textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 14.5, fontWeight: 700, color: danger ? 'var(--error)' : 'var(--ink)' }}>{label}</span>
      {onClick && !danger && WEB_ICO.chev('var(--text-secondary)', 18)}
    </button>
  );
}

/** `needsPassword` = false với tài khoản chỉ đăng nhập Google: không có mật khẩu để nhập lại, chỉ cần xác nhận. */
function DeleteDialog({ needsPassword, onCancel, onDelete }: { needsPassword: boolean; onCancel: () => void; onDelete: (password?: string) => Promise<void> }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blocked = busy || (needsPassword && !password);

  const submit = async () => {
    if (blocked) return;
    setError(null);
    setBusy(true);
    try {
      await onDelete(needsPassword ? password : undefined);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Mật khẩu không đúng.'
          : err instanceof ApiError || err instanceof NetworkError
            ? err.message
            : 'Không xoá được tài khoản. Thử lại nhé.',
      );
      setBusy(false);
    }
  };

  return (
    <WebModal onClose={onCancel} width={420}>
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'var(--font-vi)' }}>
        <h2 style={{ fontSize: 19, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Xoá tài khoản?</h2>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>Tài khoản sẽ bị khoá ngay và xoá vĩnh viễn sau 30 ngày.</p>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>
          Sau 30 ngày, email, tên hiển thị, mật khẩu và ảnh đại diện bị xoá vĩnh viễn. Dữ liệu học tập được giữ lại ở dạng ẩn danh để phục vụ thống kê.
        </p>

        {needsPassword ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="deletePassword" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
              Mật khẩu
            </label>
            <input
              id="deletePassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              style={{
                height: 46,
                borderRadius: 'var(--radius-control)',
                border: `1.5px solid ${error ? 'var(--error)' : 'var(--border-default)'}`,
                padding: '0 14px',
                fontSize: 15,
                fontFamily: 'var(--font-vi)',
                color: 'var(--ink)',
                outline: 'none',
                background: 'var(--surface-page)',
              }}
            />
            <span style={{ fontSize: 12.5, color: error ? 'var(--error)' : 'var(--text-secondary)' }} role={error ? 'alert' : undefined}>
              {error ?? 'Nhập mật khẩu để xác nhận'}
            </span>
          </div>
        ) : (
          error && (
            <span style={{ fontSize: 12.5, color: 'var(--error)' }} role="alert">
              {error}
            </span>
          )
        )}

        <button
          onClick={submit}
          disabled={blocked}
          style={{
            height: 48,
            borderRadius: 'var(--radius-control)',
            background: 'var(--error)',
            color: '#fff',
            border: 'none',
            cursor: blocked ? 'not-allowed' : 'pointer',
            opacity: blocked ? 0.5 : 1,
            fontFamily: 'var(--font-vi)',
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          {busy ? 'Đang xoá…' : 'Xoá'}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          style={{
            height: 46,
            borderRadius: 'var(--radius-control)',
            background: 'transparent',
            border: '1px solid var(--border-default)',
            color: 'var(--cta-default)',
            cursor: 'pointer',
            fontFamily: 'var(--font-vi)',
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          Huỷ
        </button>
      </div>
    </WebModal>
  );
}

interface Props {
  email?: string | null;
  /** false = tài khoản chỉ đăng nhập Google — hộp thoại xoá không hỏi mật khẩu. */
  hasPassword: boolean;
  onBack: () => void;
  onEditProfile: () => void;
  onSrsSettings: () => void;
  onLogout: () => void;
  onAbout: () => void;
  onPrivacy: () => void;
  onTerms: () => void;
  onDeleteAccount: (password?: string) => Promise<void>;
}

export function AccountSettingsScreen({ email, hasPassword, onBack, onEditProfile, onSrsSettings, onLogout, onAbout, onPrivacy, onTerms, onDeleteAccount }: Props) {
  const [confirmDel, setConfirmDel] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px 8px' }}>
        <button
          onClick={onBack}
          aria-label="Quay lại"
          style={{ width: 44, height: 44, marginLeft: -10, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {WEB_ICO.back('var(--ink)', 24)}
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Cài đặt</h1>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 32px', fontFamily: 'var(--font-vi)' }}>
        <div style={{ marginBottom: 24 }}>
          <SectionTitle>TÀI KHOẢN</SectionTitle>
          <SettingRow label={email || '—'} />
          <SettingRow label="Chỉnh sửa hồ sơ" onClick={onEditProfile} />
          <SettingRow label="Đăng xuất" onClick={onLogout} />
        </div>

        <div style={{ marginBottom: 24 }}>
          <SectionTitle>HỌC TẬP</SectionTitle>
          <SettingRow label="Cài đặt SRS" onClick={onSrsSettings} />
        </div>

        <div style={{ marginBottom: 24 }}>
          <SectionTitle>PHÁP LÝ</SectionTitle>
          <SettingRow label="Giới thiệu & Giấy phép" onClick={onAbout} />
          <SettingRow label="Privacy Policy" onClick={onPrivacy} />
          <SettingRow label="Điều khoản" onClick={onTerms} />
        </div>

        <SettingRow label="Xoá tài khoản" danger onClick={() => setConfirmDel(true)} />
      </div>

      {confirmDel && <DeleteDialog needsPassword={hasPassword} onCancel={() => setConfirmDel(false)} onDelete={onDeleteAccount} />}
    </div>
  );
}
